// src/components/ui/Modal.tsx
import React, { Fragment, ReactNode, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { cx, modalTheme } from "./styles";
import { failureSaveToast, successToast } from "../../helpers/successToast";
import { Button } from "./button";

type ModalSize = keyof typeof modalTheme.sizes;
type ModalOffset = "none" | "sm" | "md" | "lg";

const OFFSET_CLASSES: Record<ModalOffset, string> = {
  none: "",
  sm: "pt-10 sm:pt-12",
  md: "pt-16 sm:pt-20",
  lg: "pt-24 sm:pt-28",
};

export function Modal({
  open,
  onClose,
  size = "lg",
  children,
  initialFocusRef,
  dismissible = true,
  offset = "md", // default “a bit lower”
}: {
  open: boolean;
  onClose: () => void;
  size?: ModalSize;
  children: ReactNode;
  initialFocusRef?: React.RefObject<HTMLElement>;
  dismissible?: boolean;
  offset?: ModalOffset;
}) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={dismissible ? onClose : () => {}}
        initialFocus={initialFocusRef || undefined}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={modalTheme.overlay} />
        </Transition.Child>

        <div className={cx(modalTheme.container, OFFSET_CLASSES[offset])}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel className={cx(modalTheme.panel, modalTheme.sizes[size])}>
              {children}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

export function ModalHeader({
  title,
  rightSlot,
  onClose,
}: {
  title: ReactNode;
  rightSlot?: ReactNode;
  onClose?: () => void;
}) {
  return (
    <div className={modalTheme.header}>
      <Dialog.Title className={modalTheme.title}>{title}</Dialog.Title>
      <div className="flex items-center gap-2">
        {rightSlot}
        {onClose && (
          <button
            className="w-7 h-7 rounded-[6px] border border-[var(--sc-border)] flex items-center justify-center text-[var(--sc-muted)] hover:text-[var(--sc-text)] hover:bg-[var(--sc-bg)] text-sm transition-colors"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}

export function ModalBody({ children }: { children: ReactNode }) {
  return <div className={modalTheme.body}>{children}</div>;
}

export function ModalFooter({
  left,
  right,
  // delete
  onDelete,
  deleteLabel = "Usuń",
  deleteDisabled,
  deleteLoading,
  // NEW: save/cancel defaults
  onSave,
  saveLabel = "Zapisz",
  saveDisabled,
  saveLoading,
  onCancel,
  cancelLabel = "Anuluj",
  cancelDisabled,
}: {
  left?: ReactNode;
  right?: ReactNode;

  /** If provided, a default red-text delete button will be rendered on the left (unless `left` is set). */
  onDelete?: () => void | Promise<void>;
  deleteLabel?: string;
  deleteDisabled?: boolean;
  deleteLoading?: boolean;

  /** Default right-side actions (rendered only if `right` is not provided). */
  onSave?: () => void | Promise<void>;
  saveLabel?: string;
  saveDisabled?: boolean;
  saveLoading?: boolean;
  onCancel?: () => void | Promise<void>;
  cancelLabel?: string;
  cancelDisabled?: boolean;
}) {
  const [internalDeleting, setInternalDeleting] = useState(false);
  const [internalSaving, setInternalSaving] = useState(false);

  const effectiveSaving =
    typeof saveLoading === "boolean" ? saveLoading : internalSaving;

  const handleDelete = async () => {
    if (!onDelete || internalDeleting || deleteDisabled || deleteLoading) return;
    try {
      setInternalDeleting(true);
      await onDelete();
    }  catch{
            failureSaveToast('');
    }
    finally {
      setInternalDeleting(false);
    }
  };

  const handleSave = async () => {
    if (!onSave || effectiveSaving || saveDisabled) return;
    try {
      // if caller doesn't control loading, we do
      if (typeof saveLoading !== "boolean") setInternalSaving(true);
      await onSave();
            successToast();
    } 
    catch{
            failureSaveToast('');
    }
    finally {
      if (typeof saveLoading !== "boolean") setInternalSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!onCancel || cancelDisabled || effectiveSaving) return;
    await onCancel();
  };

  const showDefaultDelete = !left && !!onDelete;
  const showDefaultRight = !right && (onSave || onCancel);

  return (
    <div className={modalTheme.footer}>
      <div>
        {left ??
          (showDefaultDelete && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              disabled={deleteDisabled || deleteLoading || internalDeleting}
            >
              {deleteLoading || internalDeleting ? "Usuwanie…" : deleteLabel}
            </Button>
          ))}
      </div>

      <div className="flex gap-2">
        {right ??
          (showDefaultRight && (
            <>
              {onCancel && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCancel}
                  disabled={cancelDisabled || effectiveSaving}
                >
                  {cancelLabel}
                </Button>
              )}
              {onSave && (
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={saveDisabled || effectiveSaving}
                >
                  {effectiveSaving ? "Zapisywanie…" : saveLabel}
                </Button>
              )}
            </>
          ))}
      </div>
    </div>
  );
}
